import { ReactNode, Ref } from 'react';

import {
  FormRowLayout,
  FormRowLayoutProps,
  LabelWithDescriptionProps,
} from '@cast/design-system';
import { FieldProps } from 'react-final-form';

import { RffControlLabel } from './RffControlLabel';
import { RffLabelWithDescription } from './RffLabelWithDescription';

export type RffFormRowProps = {
  ref?: Ref<HTMLDivElement>;
  /**
   * The control to pair with the label. When provided the label block renders
   * as `RffControlLabel` (label click activates the control via a native
   * label association); otherwise as `RffLabelWithDescription`.
   */
  control?: ReactNode;
  /** Name of the form field whose validation error renders under the label block. */
  name: string;
  fieldProps?: Partial<FieldProps<string, any>>;
  /** Right-column content — the control(s) for this row. */
  children?: ReactNode;
} & Omit<LabelWithDescriptionProps, 'error' | 'slotProps'> &
  Pick<
    FormRowLayoutProps,
    'topDivider' | 'bottomDivider' | 'isLoading' | 'className' | 'sx' | 'testId'
  >;

/**
 * Two-column form row on top of the design-system `FormRowLayout`: the label
 * block on the left — `RffControlLabel` when `control` is provided, else
 * `RffLabelWithDescription` — and `children` on the right. The label block
 * binds the `name` field itself (validation error gated by
 * `showErrorOnChange`, `disabled` resolved from the form mode and the nearest
 * `RffControlsProvider`); the row's controls bind their own fields.
 *
 * `slotProps` is intentionally not exposed: the two label variants type it
 * differently, so the row accepts only the shared label props.
 */
export const RffFormRow = ({
  ref,
  control,
  children,
  fieldProps = {},
  name: _name,
  topDivider,
  bottomDivider,
  isLoading,
  className,
  sx,
  testId,
  ...labelProps
}: RffFormRowProps) => {
  return (
    <FormRowLayout
      ref={ref}
      startColumn={
        control ? (
          <RffControlLabel
            {...labelProps}
            control={control}
            name={_name}
            fieldProps={fieldProps}
          />
        ) : (
          <RffLabelWithDescription
            {...labelProps}
            name={_name}
            fieldProps={fieldProps}
          />
        )
      }
      endColumn={children}
      topDivider={topDivider}
      bottomDivider={bottomDivider}
      isLoading={isLoading}
      className={className}
      sx={sx}
      testId={testId}
    />
  );
};

RffFormRow.displayName = 'RffFormRow';
